import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import { setAccessToken } from "../lib/token-store";

export interface AuthUser {
  uuid: string;
  email: string;
  fullName?: string;
  isPlatformAdmin: boolean;
}

export interface AuthOrganisation {
  uuid: string;
  name: string;
  slug: string;
  tier: "Free_Auditor" | "Free_Supplier" | "Standard" | "Enterprise";
  role: "Admin" | "Contributor" | "Auditor_Viewer";
  activeModules: string[];
}

interface AuthState {
  status: "loading" | "anonymous" | "authenticated";
  user: AuthUser | null;
  activeOrganisation: AuthOrganisation | null;
}

interface AuthContextValue extends AuthState {
  signup: (input: SignupInput) => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

interface SignupInput {
  fullName: string;
  email: string;
  password: string;
  organisationName: string;
  organisationSlug: string;
  isSupplier?: boolean;
}

interface LoginInput {
  email: string;
  password: string;
}

interface AuthResponse {
  accessToken: string;
  user: AuthUser & { fullName: string };
  organisation: AuthOrganisation | null;
}

interface RefreshResponse {
  accessToken: string;
}

interface MeResponse {
  user: AuthUser;
  organisations: AuthOrganisation[];
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
    activeOrganisation: null,
  });

  const refreshSession = useCallback(async () => {
    try {
      const refreshed = await api<RefreshResponse>("/auth/refresh", {
        method: "POST",
        skipAuth: true,
      });
      setAccessToken(refreshed.accessToken);
      const me = await api<MeResponse>("/auth/me");
      setState({
        status: "authenticated",
        user: me.user,
        activeOrganisation: me.organisations[0] ?? null,
      });
    } catch {
      setAccessToken(null);
      setState({ status: "anonymous", user: null, activeOrganisation: null });
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const signup = useCallback(async (input: SignupInput) => {
    const response = await api<AuthResponse>("/auth/signup", {
      method: "POST",
      body: input,
      skipAuth: true,
    });
    setAccessToken(response.accessToken);
    setState({
      status: "authenticated",
      user: response.user,
      activeOrganisation: response.organisation,
    });
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const response = await api<AuthResponse>("/auth/login", {
      method: "POST",
      body: input,
      skipAuth: true,
    });
    setAccessToken(response.accessToken);
    setState({
      status: "authenticated",
      user: response.user,
      activeOrganisation: response.organisation,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST", skipAuth: true });
    } finally {
      setAccessToken(null);
      setState({ status: "anonymous", user: null, activeOrganisation: null });
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signup, login, logout, refreshSession }),
    [state, signup, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
